import type { LoginAvatar, LoginAvatarTransform, LoginBreakpoint } from "@htzap/contracts";
import { useRef, type KeyboardEvent, type PointerEvent, type WheelEvent } from "react";
import { avatarImageUrl, clampTransform, transformFor } from "./login-composition";
import "./login-avatar-canvas.css";

export function LoginAvatarCanvas({ avatars, breakpoint, selectedId, highlightedId, interactive = false, guides = false, onSelect, onTransform, onInteractionStart, onInteractionEnd }: {
  avatars: LoginAvatar[];
  breakpoint: LoginBreakpoint;
  selectedId?: string | null;
  highlightedId?: string | null;
  interactive?: boolean;
  guides?: boolean;
  onSelect?: (id: string) => void;
  onTransform?: (id: string, transform: LoginAvatarTransform, transient?: boolean) => void;
  onInteractionStart?: () => void;
  onInteractionEnd?: () => void;
}) {
  const stage = useRef<HTMLDivElement>(null);
  const drag = useRef<{ id: string; pointerId: number; dx: number; dy: number } | null>(null);
  const visible = avatars.filter((avatar) => avatar.isActive).sort((a, b) => a.zIndex - b.zIndex || a.id.localeCompare(b.id));
  function move(event: PointerEvent<HTMLDivElement>) {
    if (!drag.current || !stage.current || event.pointerId !== drag.current.pointerId) return;
    const rect = stage.current.getBoundingClientRect();
    onTransform?.(drag.current.id, clampTransform({ ...transformFor(avatars.find((item) => item.id === drag.current!.id)!, breakpoint), x: (event.clientX - rect.left - drag.current.dx) / rect.width, y: (event.clientY - rect.top - drag.current.dy) / rect.height }), true);
  }
  function keyboard(event: KeyboardEvent<HTMLButtonElement>, avatar: LoginAvatar) {
    if (!onTransform || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    const step = event.shiftKey ? 0.02 : 0.004;
    const value = transformFor(avatar, breakpoint);
    onTransform(avatar.id, clampTransform({ ...value, x: value.x + (event.key === "ArrowLeft" ? -step : event.key === "ArrowRight" ? step : 0), y: value.y + (event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0) }));
  }
  function wheel(event: WheelEvent<HTMLButtonElement>, avatar: LoginAvatar) {
    if (!onTransform || selectedId !== avatar.id) return;
    event.preventDefault();
    const value = transformFor(avatar, breakpoint);
    onTransform(avatar.id, clampTransform({ ...value, scale: value.scale + (event.deltaY < 0 ? 0.025 : -0.025) }));
  }
  return (
    <div ref={stage} className={`login-avatar-canvas login-avatar-canvas-${breakpoint}`} data-guides={guides} onPointerMove={move} onPointerUp={(event) => { if (drag.current?.pointerId === event.pointerId) { drag.current = null; onInteractionEnd?.(); } }} onPointerCancel={() => { drag.current = null; onInteractionEnd?.(); }}>
      {guides ? <i className="login-height-guide"><span>175 cm</span></i> : null}
      {visible.map((avatar) => {
        const transform = transformFor(avatar, breakpoint);
        const dimmed = Boolean(highlightedId && highlightedId !== avatar.id);
        const highlighted = highlightedId === avatar.id;
        const visualZIndex = selectedId === avatar.id || highlighted ? 2002 : breakpoint === "mobile" ? Math.round(transform.x * 1000) + 2 : avatar.zIndex + 2;
        return (
          <button key={avatar.id} type="button" className={`login-canvas-avatar ${selectedId === avatar.id ? "selected" : ""} ${highlighted ? "highlighted" : ""} ${dimmed ? "dimmed" : ""}`} style={{ left: `${transform.x * 100}%`, top: `${transform.y * 100}%`, height: `${transform.scale * 100}%`, zIndex: visualZIndex }} tabIndex={interactive ? 0 : -1} aria-label={interactive ? `Selecionar ${avatar.label}` : undefined} onClick={() => onSelect?.(avatar.id)} onKeyDown={(event) => keyboard(event, avatar)} onWheel={(event) => wheel(event, avatar)} onPointerDown={(event) => {
            if (!interactive || !stage.current) return;
            const rect = stage.current.getBoundingClientRect();
            onInteractionStart?.();
            drag.current = { id: avatar.id, pointerId: event.pointerId, dx: event.clientX - rect.left - transform.x * rect.width, dy: event.clientY - rect.top - transform.y * rect.height };
            event.currentTarget.setPointerCapture(event.pointerId);
            onSelect?.(avatar.id);
          }}>
            <img src={avatarImageUrl(avatar)} alt="" draggable={false} />
          </button>
        );
      })}
    </div>
  );
}
